import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { homePackages, businessFiestaPackages, businessPackages } from '../mock';
import { FileDropzone } from './FileDropzone';
import { PackageSummaryCard } from './PackageSummaryCard';
import { HomeDocsSection } from './HomeDocsSection';
import { BusinessDocsSection } from './BusinessDocsSection';
import { HomeContactFields } from './HomeContactFields';
import { BusinessContactFields } from './BusinessContactFields';

const NONE_ADDON = 'none';

const bizPromoList = (pkg) => pkg?.deviceAddonList || [];
const bizPlanLabel = (p, promo) => {
  const total = (parseFloat(p.price.replace(/[^\d.]/g, '')) || 0) + (promo?.price || 0);
  const promoText = promo ? ` + ${promo.name.replace(/\n/g, ' ')}` : '';
  return `RM${total.toFixed(2)}/mth unifi ${p.speed}${promoText} (${promo?.contractOverride || p.contract})`;
};

const RegistrationForm = ({ isOpen, onClose, selectedPackage, packageType, mode = 'modal' }) => {
  const isPageMode = mode === 'page';
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(packageType || 'home');
  const [homeSpeed, setHomeSpeed] = useState('');
  const [homeAddon, setHomeAddon] = useState(NONE_ADDON);
  const [homeDeviceAddon, setHomeDeviceAddon] = useState(NONE_ADDON);
  const [bizCategory, setBizCategory] = useState('');
  const [bizPlan, setBizPlan] = useState('');
  const [bizDeviceAddon, setBizDeviceAddon] = useState(NONE_ADDON);
  const [nationality, setNationality] = useState('malaysian');
  const [bizNationality, setBizNationality] = useState('malaysian');
  const [docs, setDocs] = useState({ icFront: [], icBack: [], utilityBill: [], passport: [], ssmDoc: [], bizPassport: [] });
  const [formData, setFormData] = useState({
    name: '', nric: '', mobile: '', email: '', address: '', postcode: '',
    installationDay: '', remarks: '',
    companyName: '', registrationNo: '', directorName: '', directorNric: '', officeAddress: '',
  });

  // Pre-fill when a package card was clicked OR from URL query params in page mode.
  // Legitimate initialise-from-props/URL pattern.
  useEffect(() => {
    if (!isPageMode && !isOpen) return;

    // In page mode, read selection from URL query params
    let pkg = selectedPackage;
    let pkgType = packageType;
    let urlType = null;
    if (isPageMode && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const speed = params.get('speed') || params.get('package');
      const promo = params.get('promo');
      const addon = params.get('addon');
      const type = params.get('type');
      // Track tab selection from URL even when no speed is provided
      if (type === 'business' || type === 'home') {
        urlType = type;
      }
      if (speed) {
        pkg = { speed, selectedOptional: addon || null, selectedDeviceAddon: promo || null };
        pkgType = type === 'business' ? 'business' : 'home';
      }
    }

    if (pkg?.speed) {
      if (pkgType === 'business') {
        setActiveTab('business');
        const inFiesta = businessFiestaPackages.find(p => p.speed === pkg.speed);
        if (inFiesta) {
          setBizCategory('fiesta');
          setBizPlan(`fiesta-${inFiesta.id}`);
        } else {
          setBizCategory('broadband');
          const inBiz = businessPackages.find(p => p.speed === pkg.speed);
          if (inBiz) {
            setBizPlan(`biz-${inBiz.id}`);
            const promoMatch = bizPromoList(inBiz).find(d => d.name === pkg.selectedDeviceAddon);
            setBizDeviceAddon(promoMatch ? promoMatch.name : NONE_ADDON);
          }
        }
      } else {
        setActiveTab('home');
        setHomeSpeed(pkg.speed);
        setHomeAddon(pkg.selectedOptional || NONE_ADDON);
        setHomeDeviceAddon(pkg.selectedDeviceAddon || NONE_ADDON);
      }
    } else if (urlType) {
      // No speed pre-selected but URL specifies which tab to open
      setActiveTab(urlType);
    }
  }, [isOpen, selectedPackage, packageType, isPageMode]);

  const currentHomePkg = useMemo(
    () => homePackages.find(p => p.speed === homeSpeed),
    [homeSpeed]
  );

  const homeDeviceAddonOptions = useMemo(() => {
    if (!currentHomePkg) return [];
    const list = currentHomePkg.deviceAddonList || (currentHomePkg.deviceAddon ? [currentHomePkg.deviceAddon] : []);
    if (list.length === 0) return [];
    const opts = [{ value: NONE_ADDON, label: 'No promo' }];
    list.forEach(item => {
      // Skip the "+RMxx/mth" prefix when the addon has a priceOverride —
      // in that case the promo price (e.g. "RM59") is already baked into
      // item.name, so adding a prefix would produce a duplicate like
      // "RM59/mth — RM59 First 10 Month...".
      const priceLabel = item.priceLabel ? item.priceLabel : `+RM${item.price}/mth`;
      const cleanName = item.name.replace(/\n/g, ' ');
      opts.push({
        value: item.name,
        label: item.priceOverride ? cleanName : `${priceLabel} — ${cleanName}`,
      });
    });
    return opts;
  }, [currentHomePkg]);

  const selectedDeviceAddonObj = useMemo(() => {
    if (!currentHomePkg || homeDeviceAddon === NONE_ADDON) return null;
    const list = currentHomePkg.deviceAddonList || (currentHomePkg.deviceAddon ? [currentHomePkg.deviceAddon] : []);
    return list.find(d => d.name === homeDeviceAddon) || null;
  }, [currentHomePkg, homeDeviceAddon]);

  // Hide Addon Apps when Free 6 Month is selected OR when package has hideAddonsByDefault and no device addon selected
  const isAddonAppsHidden = useMemo(() => {
    if (selectedDeviceAddonObj?.type === 'free-months') return true;
    if (currentHomePkg?.hideAddonsByDefault && !selectedDeviceAddonObj) return true;
    return false;
  }, [currentHomePkg, selectedDeviceAddonObj]);

  const homeAddonOptions = useMemo(() => {
    if (!currentHomePkg) return [];
    if (isAddonAppsHidden) return [];
    const opts = [{ value: NONE_ADDON, label: 'No add-on' }];
    const items = currentHomePkg.optionalList || (currentHomePkg.optional ? [currentHomePkg.optional] : []);
    items.forEach(item => {
      opts.push({
        value: item.name,
        label: `+RM${item.price}/mth — ${item.name}`,
      });
    });
    return opts;
  }, [currentHomePkg, isAddonAppsHidden]);

  // Auto-clear addon when it should be hidden by package rule (e.g. Free 6 Month promo
  // or packages with hideAddonsByDefault). Addons and promos can otherwise coexist.
  // Intentionally only depends on isAddonAppsHidden — homeAddon read is a one-shot check,
  // not something we want to re-run on every change.
  useEffect(() => {
    if (isAddonAppsHidden && homeAddon !== NONE_ADDON) setHomeAddon(NONE_ADDON);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddonAppsHidden]);

  const homeSummary = useMemo(() => {
    if (!currentHomePkg) return null;
    const basePrice = parseFloat(currentHomePkg.price.replace(/[^\d.]/g, '')) || 0;
    const items = currentHomePkg.optionalList || (currentHomePkg.optional ? [currentHomePkg.optional] : []);
    const addon = (homeAddon !== NONE_ADDON && !isAddonAppsHidden) ? items.find(i => i.name === homeAddon) : null;
    const devicePrice = selectedDeviceAddonObj ? (selectedDeviceAddonObj.price || 0) : 0;
    const total = basePrice + (addon ? addon.price : 0) + devicePrice;
    // Contract priority: deviceAddon > optional > default
    const contract = selectedDeviceAddonObj?.contractOverride
      || addon?.contractOverride
      || currentHomePkg.contract;
    // Promos with `priceOverride` completely replace the total price display
    // (e.g. "RM59 for First 10 month" with the original struck through).
    const priceOverride = selectedDeviceAddonObj?.priceOverride || null;
    return {
      speed: currentHomePkg.speed,
      basePrice,
      addon,
      deviceAddon: selectedDeviceAddonObj,
      total,
      contract,
      priceOverride,
    };
  }, [currentHomePkg, homeAddon, selectedDeviceAddonObj, isAddonAppsHidden]);

  const currentBizPkg = useMemo(() => {
    if (!bizPlan) return null;
    return businessPackages.find(p => `biz-${p.id}` === bizPlan) || null;
  }, [bizPlan]);

  const bizPromoObj = useMemo(
    () => bizPromoList(currentBizPkg).find(d => d.name === bizDeviceAddon) || null,
    [currentBizPkg, bizDeviceAddon]
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear inline error for this field as soon as user starts editing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [submittedSnapshot, setSubmittedSnapshot] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Anti-bot: timestamp when the form was first mounted/opened.
  // Submissions completed in < 10s are treated as bot traffic (silent reject).
  // useRef avoids re-renders; effect resets the timer when the form opens.
  const formMountTimeRef = useRef(0);
  useEffect(() => {
    formMountTimeRef.current = Date.now();
  }, [isOpen, isPageMode]);

  // Anti-bot: when set to true, the success modal is shown to deceive the bot
  // but we suppress Google Ads conversion + GA events so bot traffic does NOT
  // pollute our advertising data (which would otherwise inflate cost-per-lead
  // and bias smart bidding toward bot sources).
  const botFlaggedRef = useRef(false);

  // Fire Google Ads conversion tag whenever the success modal opens.
  // The base gtag.js is loaded globally from public/index.html; here we
  // re-trigger a config / page_view event so Google Ads registers this as
  // a "Submission Successful" page view for conversion tracking.
  // The Google Ads ID is configurable via REACT_APP_GOOGLE_ADS_ID env var.
  useEffect(() => {
    if (!showSuccess) return;
    // Anti-bot: if the success modal was opened by a tripped honeypot / time
    // check, DO NOT fire Google Ads conversion — that would pollute our Ads
    // data and bias smart bidding toward bot traffic sources.
    if (botFlaggedRef.current) {
      botFlaggedRef.current = false; // reset for next legitimate submit
      return;
    }
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const adsId = process.env.REACT_APP_GOOGLE_ADS_ID;
    if (!adsId) return;
    // Google Ads requires the conversion LABEL appended after the ID with a slash:
    //   send_to: 'AW-18107842688/C4HzCM7jzrscEICBv7pD'
    // Without the label, Google Ads cannot map the event to the right Conversion
    // Action — Tag Assistant will report "This conversion action wasn't detected".
    const conversionLabel = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;
    const sendTo = conversionLabel ? `${adsId}/${conversionLabel}` : adsId;
    try {
      window.gtag('event', 'conversion', {
        send_to: sendTo,
        event_category: 'registration',
        event_label: activeTab === 'business' ? 'business_submission' : 'home_submission',
      });
      window.gtag('event', 'page_view', {
        page_title: 'Application Submitted',
        page_path: '/registration_form/success',
      });
    } catch (err) {
      // gtag firing is non-critical (analytics only) — log warning but don't break user flow.
      console.warn('[Analytics] gtag conversion fire failed:', err);
    }
  }, [showSuccess, activeTab]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const mobileRegex = /^[0-9+\-\s()]+$/;

  const validateField = (field, value) => {
    let err = '';
    if (field === 'email' || field === 'bizEmail') {
      if (value && !emailRegex.test(value)) err = 'Please enter a valid email address (e.g. name@example.com).';
    }
    if (field === 'mobile' || field === 'bizMobile') {
      if (value && !mobileRegex.test(value)) err = 'Mobile number can only contain digits and symbols (+ - ( ) space).';
    }
    setFieldErrors(prev => ({ ...prev, [field]: err }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = {};

    if (activeTab === 'home') {
      if (!homeSpeed) errs.homeSpeed = 'Please select a speed plan.';
      if (!formData.name?.trim()) errs.name = 'Please enter your full name.';
      if (!formData.nric?.trim()) errs.nric = 'Please enter your NRIC / Passport number.';
      if (!formData.mobile?.trim()) {
        errs.mobile = 'Please enter your mobile number.';
      } else if (!/^[0-9+\-\s()]+$/.test(formData.mobile)) {
        errs.mobile = 'Mobile number can only contain digits and symbols (+ - ( ) space).';
      }
      if (!formData.email?.trim()) {
        errs.email = 'Please enter your email address.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errs.email = 'Please enter a valid email address (e.g. name@example.com).';
      }
      if (!formData.address?.trim()) errs.address = 'Please enter your installation address.';
      if (!formData.postcode?.trim()) errs.postcode = 'Please enter your postcode.';
      if (!formData.installationDay) errs.installationDay = 'Please select your preferred installation day.';

      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        toast({ title: 'Please complete all required fields', variant: 'destructive' });
        // Scroll to first invalid field
        setTimeout(() => {
          const firstKey = Object.keys(errs)[0];
          const el = document.querySelector(`[data-field-error="${firstKey}"]`) || document.getElementById(firstKey);
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
        return;
      }
    } else {
      if (!bizPlan) errs.bizPlan = 'Please select a business plan.';
      if (!formData.companyName?.trim()) errs.companyName = 'Please enter the company name.';
      if (!formData.registrationNo?.trim()) errs.registrationNo = 'Please enter the business registration number.';
      if (!formData.directorName?.trim()) errs.directorName = 'Please enter the director full name.';
      if (!formData.directorNric?.trim()) errs.directorNric = 'Please enter the director NRIC / Passport number.';
      if (!formData.mobile?.trim()) {
        errs.bizMobile = 'Please enter the mobile number.';
      } else if (!/^[0-9+\-\s()]+$/.test(formData.mobile)) {
        errs.bizMobile = 'Mobile number can only contain digits and symbols (+ - ( ) space).';
      }
      if (!formData.email?.trim()) {
        errs.bizEmail = 'Please enter the email address.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errs.bizEmail = 'Please enter a valid email address.';
      }
      if (!formData.officeAddress?.trim()) errs.officeAddress = 'Please enter the office installation address.';
      if (!formData.postcode?.trim()) errs.postcode = 'Please enter the postcode.';
      if (!formData.installationDay) errs.installationDay = 'Please select your preferred installation day.';
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        toast({ title: 'Please complete all required fields', variant: 'destructive' });
        setTimeout(() => {
          const firstKey = Object.keys(errs)[0];
          const el = document.querySelector(`[data-field-error="${firstKey}"]`) || document.getElementById(firstKey);
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
        return;
      }
    }

    setFieldErrors({});

    // ─── Anti-bot checks run AFTER field validation passes ─────────────
    // Doing it in this order ensures real users with incomplete forms get
    // proper error messages first, instead of a silent fake-success modal.

    // Layer 1: Honeypot — hidden "Nickname" field invisible to real users
    // (opacity:0 + zero-size, sits in the natural DOM flow). Headless bots
    // that iterate over inputs fill it; real users cannot focus or see it.
    // If filled, silently show success so the bot thinks it succeeded.
    const honeypot = e.target.elements?.Nickname;
    if (honeypot && honeypot.value && honeypot.value.trim() !== '') {
      console.warn('[Anti-bot] Honeypot tripped');
      botFlaggedRef.current = true;
      setShowSuccess(true);
      return;
    }

    // Layer 2: Time check (≥ 10 seconds) — filling a real multi-field form
    // takes time. A submission finished within 10s of opening = almost
    // certainly a bot. With proper validation above, real users physically
    // cannot complete the form this fast anyway.
    const elapsed = Date.now() - formMountTimeRef.current;
    if (elapsed < 10000) {
      console.warn(`[Anti-bot] Submitted too fast (${elapsed}ms)`);
      botFlaggedRef.current = true;
      setShowSuccess(true);
      return;
    }

    const payload = new FormData();
    payload.append('access_key', process.env.REACT_APP_WEB3FORMS_KEY);

    // Friendly label maps so the email reflects exactly what the user saw
    const installDayLabels = {
      asap: 'As Soon As Possible',
      weekday: 'Weekday Only (Monday - Friday)',
      weekend: 'Weekend Only (Saturday - Sunday)',
    };

    const ORANGE = '#FF6600';
    const renderRow = (label, value) => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #eee;font-weight:600;color:#444;width:200px;vertical-align:top;">${label}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #eee;color:#111;">${value || '-'}</td>
      </tr>`;

    // Helper to append a single file or an array of files to the FormData payload
    const appendFiles = (label, list) => {
      // Normalize: accept either an array, a single File, or null/undefined
      let arr = [];
      if (Array.isArray(list)) arr = list;
      else if (list) arr = [list];
      if (arr.length === 1) {
        payload.append(label, arr[0]);
      } else {
        arr.forEach((f, i) => payload.append(`${label} ${i + 1}`, f));
      }
    };

    if (activeTab === 'home') {
      const deviceLabel = homeSummary?.deviceAddon ? ` + ${homeSummary.deviceAddon.name.replace(/\n/g, ' ')}` : '';
      const addonLabel = homeSummary?.addon ? ` + ${homeSummary.addon.name}` : '';
      const priceHead = homeSummary?.priceOverride
        ? `${homeSummary.priceOverride.amount} ${homeSummary.priceOverride.period}${homeSummary.priceOverride.strikeThrough ? ` (was ${homeSummary.priceOverride.strikeThrough})` : ''}`
        : `RM${homeSummary?.total.toFixed(2)}/mth`;
      const planLabel = homeSummary
        ? `${priceHead} unifi ${homeSummary.speed}${deviceLabel}${addonLabel} (${homeSummary.contract})`
        : homeSpeed;
      const installDay = installDayLabels[formData.installationDay] || formData.installationDay;

      payload.append('subject', `UNIFI REGISTRATION : ${formData.name} : ${formData.nric}`.toUpperCase());
      payload.append('from_name', 'UNIFI.NET REGISTRATION');
      payload.append('replyto', formData.email);

      // Individual fields (Web3Forms auto-renders these into a clean email)
      payload.append('Form Type', 'HOME');
      payload.append('Package', planLabel);
      payload.append('Limited Time Promo', homeDeviceAddon === NONE_ADDON ? 'None' : homeDeviceAddon.replace(/\n/g, ' '));
      payload.append('Add-on', homeAddon === NONE_ADDON ? 'None' : homeAddon);
      payload.append('Full Name', formData.name);
      payload.append('New NRIC / Passport Number', formData.nric);
      payload.append('Mobile No.', formData.mobile);
      payload.append('Email Address', formData.email);
      payload.append('Unifi Installation Address', formData.address);
      payload.append('Postcode', formData.postcode);
      payload.append('Preferred Installation Day', installDay);
      payload.append('Additional Remarks', formData.remarks || '-');
      payload.append('Documents Registration Verification Require', nationality === 'malaysian' ? 'Malaysian' : 'Non Malaysian');

      if (nationality === 'malaysian') {
        appendFiles('MyKad IC Front', docs.icFront);
        appendFiles('MyKad IC Back', docs.icBack);
        appendFiles('Utility Bill', docs.utilityBill);
      } else {
        appendFiles('Passport', docs.passport);
      }
    } else {
      let planLabel = bizPlan;
      if (bizPlan?.startsWith('fiesta-')) {
        const p = businessFiestaPackages.find(x => `fiesta-${x.id}` === bizPlan);
        if (p) {
          const priceNum = parseFloat(p.price.replace(/[^\d.]/g, '')) || 0;
          planLabel = `RM${priceNum.toFixed(2)}/mth unifi Biz Fiesta ${p.speed} + ${p.device} (${p.contract})`;
        }
      } else if (bizPlan?.startsWith('biz-')) {
        const p = businessPackages.find(x => `biz-${x.id}` === bizPlan);
        if (p) planLabel = bizPlanLabel(p, bizPromoObj);
      }

      payload.append('subject', `UNIFI REGISTRATION : ${formData.directorName || formData.companyName} : ${formData.directorNric}`.toUpperCase());
      payload.append('from_name', 'UNIFI.NET REGISTRATION');
      payload.append('replyto', formData.email);
      payload.append('Form Type', 'BUSINESS');
      payload.append('Package', planLabel);
      payload.append('Limited Time Promo', bizPromoObj ? bizPromoObj.name.replace(/\n/g, ' ') : 'None');
      payload.append('Company Name', formData.companyName);
      payload.append('Business Registration Number', formData.registrationNo);
      payload.append('Director Full Name', formData.directorName);
      payload.append('Director NRIC No./Passport No.', formData.directorNric);
      payload.append('Mobile No.', formData.mobile);
      payload.append('Email Address', formData.email);
      payload.append('Office Installation Address', formData.officeAddress);
      payload.append('Postcode', formData.postcode);
      payload.append('Preferred Installation Day', installDayLabels[formData.installationDay] || formData.installationDay);
      payload.append('Director Nationality', bizNationality === 'malaysian' ? 'Malaysian' : 'Non Malaysian');
      appendFiles('Company SSM Document', docs.ssmDoc);
      if (bizNationality === 'malaysian') {
        appendFiles('Director MyKad IC Front', docs.icFront);
        appendFiles('Director MyKad IC Back', docs.icBack);
      } else {
        appendFiles('Director Passport', docs.bizPassport);
      }
      appendFiles('Installation Address Utility Bill', docs.utilityBill);
    }

    setSubmitting(true);
    try {
      let res;
      try {
        res = await fetch(process.env.REACT_APP_WEB3FORMS_URL, { method: 'POST', body: payload });
      } catch (networkErr) {
        throw new Error('Network error — please disable ad blockers / privacy extensions and try again.');
      }
      // Treat any successful HTTP status (2xx) as success even if the body can't be read.
      // Web3Forms returns 200 on success. We avoid response.json()/text() because some
      // browser extensions intercept the response stream and make it unreadable.
      if (!res.ok && res.status !== 0) {
        // Attempt to read the error body; log if unreadable so we can debug failed submissions
        let detail = '';
        try {
          detail = await res.text();
        } catch (err) {
          console.warn('[RegistrationForm] Could not read error response body:', err);
        }
        let parsed = {};
        try {
          parsed = JSON.parse(detail);
        } catch (err) {
          console.warn('[RegistrationForm] Error response was not JSON:', detail?.slice(0, 200));
        }
        throw new Error(parsed.message || `Submission failed (HTTP ${res.status}).`);
      }

      toast({
        title: 'Application Submitted!',
        description: 'Your unifi application has been submitted successfully.',
      });

      // Build a snapshot of submitted details (excludes file uploads) for success modal
      const installDay = installDayLabels[formData.installationDay] || formData.installationDay;
      if (activeTab === 'home') {
        const deviceLabel = homeSummary?.deviceAddon ? ` + ${homeSummary.deviceAddon.name.replace(/\n/g, ' ')}` : '';
        const addonLabelText = homeSummary?.addon ? ` + ${homeSummary.addon.name}` : '';
        const priceHeadSnap = homeSummary?.priceOverride
          ? `${homeSummary.priceOverride.amount} ${homeSummary.priceOverride.period}${homeSummary.priceOverride.strikeThrough ? ` (was ${homeSummary.priceOverride.strikeThrough})` : ''}`
          : `RM${homeSummary?.total.toFixed(2)}/mth`;
        const planLabelSnap = homeSummary
          ? `${priceHeadSnap} unifi ${homeSummary.speed}${deviceLabel}${addonLabelText} (${homeSummary.contract})`
          : homeSpeed;
        setSubmittedSnapshot({
          type: 'home',
          rows: [
            ['Package', planLabelSnap],
            ['Limited Time Promo', homeDeviceAddon === NONE_ADDON ? '—' : homeDeviceAddon.replace(/\n/g, ' ')],
            ['Add-on App', homeAddon === NONE_ADDON ? '—' : homeAddon],
            ['Full Name', formData.name],
            ['NRIC / Passport', formData.nric],
            ['Mobile', formData.mobile],
            ['Email', formData.email],
            ['Installation Address', formData.address],
            ['Postcode', formData.postcode],
            ['Preferred Installation Day', installDay],
            ['Remarks', formData.remarks || '—'],
          ],
        });
      } else {
        let planLabelSnap = bizPlan;
        if (bizPlan?.startsWith('fiesta-')) {
          const p = businessFiestaPackages.find(x => `fiesta-${x.id}` === bizPlan);
          if (p) {
            const priceNum = parseFloat(p.price.replace(/[^\d.]/g, '')) || 0;
            planLabelSnap = `RM${priceNum.toFixed(2)}/mth unifi Biz Fiesta ${p.speed} + ${p.device} (${p.contract})`;
          }
        } else if (bizPlan?.startsWith('biz-')) {
          const p = businessPackages.find(x => `biz-${x.id}` === bizPlan);
          if (p) planLabelSnap = bizPlanLabel(p, bizPromoObj);
        }
        setSubmittedSnapshot({
          type: 'business',
          rows: [
            ['Package', planLabelSnap],
            ['Limited Time Promo', bizPromoObj ? bizPromoObj.name.replace(/\n/g, ' ') : '—'],
            ['Company Name', formData.companyName],
            ['Business Registration Number', formData.registrationNo],
            ['Director Full Name', formData.directorName],
            ['Director NRIC / Passport', formData.directorNric],
            ['Mobile', formData.mobile],
            ['Email', formData.email],
            ['Office Installation Address', formData.officeAddress],
            ['Postcode', formData.postcode],
            ['Preferred Installation Day', installDay],
          ],
        });
      }

      setShowSuccess(true);

      setHomeSpeed('');
      setHomeAddon(NONE_ADDON);
      setBizCategory('');
      setBizPlan('');
      setBizDeviceAddon(NONE_ADDON);
      setDocs({ icFront: [], icBack: [], utilityBill: [], passport: [], ssmDoc: [], bizPassport: [] });
      setNationality('malaysian');
      setBizNationality('malaysian');
      setFormData({
        name: '', nric: '', mobile: '', email: '', address: '', postcode: '',
        installationDay: '', remarks: '',
        companyName: '', registrationNo: '', directorName: '', directorNric: '', officeAddress: '',
      });
      // Close the form so the success modal sits on top of the page (not the form)
      if (onClose) onClose();
    } catch (err) {
      // Friendly error: hide raw Web3Forms/network details and steer the user
      // to WhatsApp for manual registration via a modal popup (not a toast).
      // The technical error is still logged to console for debugging.
      console.warn('[RegistrationForm] Submission failed:', err);
      setShowError(true);
    } finally {
      setSubmitting(false);
    }
  };

  // NOTE: Do NOT define FormWrapper as a component inside this function — that
  // recreates the component identity on every render and causes input focus loss
  // when typing. Use inline conditional rendering instead.

  const HeaderEl = isPageMode
    ? (
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#FF6600]">REGISTER UNIFI</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Fill in the form below to register for Unifi broadband service
          </p>
        </div>
      )
    : (
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#FF6600]">REGISTER UNIFI</DialogTitle>
          <DialogDescription className="text-gray-600">
            Fill in the form below to register for Unifi broadband service
          </DialogDescription>
        </DialogHeader>
      );

  return (
    <>
    {isPageMode ? (
      <div className="w-full">
        {HeaderEl}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="home" data-testid="reg-tab-home">HOME</TabsTrigger>
            <TabsTrigger value="business" data-testid="reg-tab-business">BUSINESS</TabsTrigger>
          </TabsList>

          {/* HOME REGISTRATION FORM */}
          <TabsContent value="home">
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {/* Package selector – two-step */}
              <div className="rounded-xl border border-gray-200 p-5 bg-gray-50/50 space-y-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Your Package</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Speed Plan <span className="text-red-500">*</span></Label>
                    <Select value={homeSpeed} onValueChange={(v) => {
                      // Guard: Radix Select can fire onValueChange("") spuriously when the
                      // controlled value is set programmatically (e.g. via URL pre-fill).
                      // Ignore empty values so the pre-filled selection is preserved.
                      if (!v) return;
                      setHomeSpeed(v);
                      setHomeAddon(NONE_ADDON);
                      setHomeDeviceAddon(NONE_ADDON);
                      setFieldErrors(prev => { const n = { ...prev }; delete n.homeSpeed; return n; });
                    }}>
                      <SelectTrigger data-testid="reg-home-speed" data-field-error="homeSpeed" className={fieldErrors.homeSpeed ? 'border-red-500 focus:ring-red-500' : ''}>
                        <SelectValue placeholder="Select speed" />
                      </SelectTrigger>
                      <SelectContent>
                        {homePackages.map(pkg => (
                          <SelectItem key={pkg.id} value={pkg.speed}>
                            {pkg.price}/mth — unifi {pkg.speed}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.homeSpeed && <p className="text-xs text-red-500">{fieldErrors.homeSpeed}</p>}
                  </div>
                  {homeDeviceAddonOptions.length > 0 && (
                    <div className="space-y-2">
                      <Label>Limited Time Promo</Label>
                      <Select value={homeDeviceAddon} onValueChange={setHomeDeviceAddon} disabled={!currentHomePkg}>
                        <SelectTrigger data-testid="reg-home-device-addon">
                          <SelectValue placeholder="Select promo" />
                        </SelectTrigger>
                        <SelectContent>
                          {homeDeviceAddonOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {!isAddonAppsHidden && homeAddonOptions.length > 1 && (
                    <div className="space-y-2">
                      <Label>Add-on App</Label>
                      <Select value={homeAddon} onValueChange={setHomeAddon} disabled={!currentHomePkg}>
                        <SelectTrigger data-testid="reg-home-addon">
                          <SelectValue placeholder={currentHomePkg ? 'Select add-on' : 'Choose speed first'} />
                        </SelectTrigger>
                        <SelectContent>
                          {homeAddonOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Live summary */}
                {homeSummary && (
                  <PackageSummaryCard
                    planName={homeSummary.speed}
                    priceText={homeSummary.priceOverride ? homeSummary.priceOverride.amount : `RM${homeSummary.total.toFixed(2)}`}
                    priceSuffix={homeSummary.priceOverride ? homeSummary.priceOverride.period : '/month'}
                    priceStrikeThrough={homeSummary.priceOverride?.strikeThrough}
                    contract={homeSummary.contract}
                    addonLines={[
                      homeSummary.deviceAddon && (
                        homeSummary.deviceAddon.priceOverride
                          ? homeSummary.deviceAddon.name.replace(/\n/g, ' ')
                          : `+ ${homeSummary.deviceAddon.name.replace(/\n/g, ' ')}${homeSummary.deviceAddon.price > 0 ? ` (+RM${homeSummary.deviceAddon.price})` : ''}`
                      ),
                      homeSummary.addon && `+ ${homeSummary.addon.name} (+RM${homeSummary.addon.price})`,
                    ].filter(Boolean)}
                    recommended={currentHomePkg?.recommended}
                    users={currentHomePkg?.users}
                    includes={[
                      ...(currentHomePkg?.includes || []),
                      ...(homeSummary.deviceAddon?.includesAdd || []),
                    ]}
                  />
                )}
              </div>

              <HomeContactFields
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
                validateField={validateField}
              />

              {/* Documents Registration Verification */}
              <HomeDocsSection nationality={nationality} setNationality={setNationality} docs={docs} setDocs={setDocs} />

              <div className="space-y-2">
                <Label htmlFor="remarks">Additional Remarks</Label>
                <Textarea id="remarks" value={formData.remarks} onChange={(e) => handleInputChange('remarks', e.target.value)} placeholder="Any additional information..." rows={3} />
              </div>

              <Button type="submit" disabled={submitting} data-testid="reg-home-submit" className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white py-6 text-lg font-semibold disabled:opacity-60">
                {submitting ? 'Submitting…' : 'Submit Application'}
              </Button>
            </form>
          </TabsContent>

          {/* BUSINESS REGISTRATION FORM */}
          <TabsContent value="business">
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              <div className="rounded-xl border border-gray-200 p-5 bg-gray-50/50 space-y-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Your Package</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Speed Plan <span className="text-red-500">*</span></Label>
                    <Select value={bizPlan} onValueChange={(v) => { if (!v) return; setBizPlan(v); setBizDeviceAddon(NONE_ADDON); setFieldErrors(prev => { const n={...prev}; delete n.bizPlan; return n; }); }}>
                      <SelectTrigger data-testid="reg-biz-plan" data-field-error="bizPlan" className={fieldErrors.bizPlan ? 'border-red-500 focus:ring-red-500' : ''}>
                        <SelectValue placeholder="Select speed" />
                      </SelectTrigger>
                      <SelectContent>
                        {businessPackages.map(p => (
                          <SelectItem key={p.id} value={`biz-${p.id}`}>
                            {p.price}/mth — unifi {p.speed}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.bizPlan && <p className="text-xs text-red-500">{fieldErrors.bizPlan}</p>}
                  </div>
                  {bizPromoList(currentBizPkg).length > 0 && (
                    <div className="space-y-2">
                      <Label>Limited Time Promo</Label>
                      <Select value={bizDeviceAddon} onValueChange={setBizDeviceAddon}>
                        <SelectTrigger data-testid="reg-biz-device-addon">
                          <SelectValue placeholder="Select promo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE_ADDON}>No promo</SelectItem>
                          {bizPromoList(currentBizPkg).map(opt => (
                            <SelectItem key={opt.name} value={opt.name}>
                              {`${opt.priceLabel || `+RM${opt.price}/mth`} — ${opt.name.replace(/\n/g, ' ')}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Live business package summary */}
                {currentBizPkg && (
                  <PackageSummaryCard
                    planName={currentBizPkg.speed}
                    priceText={`RM${((parseFloat(currentBizPkg.price.replace(/[^\d.]/g, '')) || 0) + (bizPromoObj?.price || 0)).toFixed(2)}`}
                    contract={bizPromoObj?.contractOverride || currentBizPkg.contract}
                    addonLines={bizPromoObj ? [`+ ${bizPromoObj.name.replace(/\n/g, ' ')}${bizPromoObj.price > 0 ? ` (+RM${bizPromoObj.price})` : ''}`] : []}
                    recommended={currentBizPkg.recommended}
                    users={currentBizPkg.users}
                    includes={currentBizPkg.includes}
                  />
                )}
              </div>

              <BusinessContactFields
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
                validateField={validateField}
              />

              {/* Documents Registration Verification (Business) */}
              <BusinessDocsSection bizNationality={bizNationality} setBizNationality={setBizNationality} docs={docs} setDocs={setDocs} />

              <Button type="submit" disabled={submitting} data-testid="reg-biz-submit" className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white py-6 text-lg font-semibold disabled:opacity-60">
                {submitting ? 'Submitting…' : 'Submit Application'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    ) : (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <p className="text-sm text-gray-500 p-6 text-center">
            Please open the standalone registration page to complete your application.
          </p>
        </DialogContent>
      </Dialog>
    )}

    {/* Centered error modal — friendly fallback that redirects user to WhatsApp */}
    <Dialog open={showError} onOpenChange={setShowError}>
      <DialogContent className="max-w-md text-center max-h-[90vh] overflow-y-auto" data-testid="error-modal">
        <div className="flex flex-col items-center py-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <svg className="w-9 h-9 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Failed to submit</h3>
          <p className="text-sm text-gray-700 mb-5 leading-relaxed">
            Don&apos;t worry — WhatsApp us now and we&apos;ll register you manually within minutes!
          </p>

          <div className="flex gap-3 w-full">
            <Button
              type="button"
              onClick={() => setShowError(false)}
              variant="outline"
              className="flex-1"
              data-testid="error-close"
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => window.open(`https://api.whatsapp.com/send?phone=${process.env.REACT_APP_WHATSAPP_PHONE}&text=I%27m%20interested%20UniFi%20Service%2C%20but%20i%20failed%20to%20submit%20application%20online%2C%20could%20you%20assist%20me%3F`, '_blank')}
              className="flex-1 bg-[#25D366] hover:bg-[#1ebe57] text-white"
              data-testid="error-whatsapp"
            >
              WhatsApp Us
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Centered success modal */}
    <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
      <DialogContent className="max-w-md text-center max-h-[90vh] overflow-y-auto">
        <div className="flex flex-col items-center py-4">
          <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-4">
            <svg className="w-9 h-9 text-[#FF6600]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Application Submitted!</h3>
          <p className="text-sm text-gray-700 mb-2 leading-relaxed">
            Your unifi application has been submitted successfully. We will get in touch with you within 1 business day.
          </p>
          <p className="text-sm text-gray-600 mb-5 leading-relaxed">
            If you have any questions or need further assistance, feel free to reach out to us on WhatsApp.
          </p>

          {submittedSnapshot && (
            <div className="w-full mb-5 rounded-lg border border-orange-200 bg-orange-50/40 overflow-hidden text-left">
              <div className="px-4 py-2.5 bg-orange-100/60 border-b border-orange-200">
                <p className="text-[11px] font-bold tracking-[0.12em] text-[#FF6600] uppercase">Submission Summary</p>
              </div>
              <dl className="divide-y divide-orange-100">
                {submittedSnapshot.rows.map(([label, value]) => (
                  <div key={label} className="flex items-start gap-3 px-4 py-2.5">
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 w-32 flex-shrink-0 pt-0.5">{label}</dt>
                    <dd className="text-sm text-gray-900 flex-1 break-words whitespace-pre-wrap">{value || '—'}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="flex gap-3 w-full">
            <Button
              type="button"
              onClick={() => setShowSuccess(false)}
              variant="outline"
              className="flex-1"
              data-testid="success-close"
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => window.open(`https://api.whatsapp.com/send?phone=${process.env.REACT_APP_WHATSAPP_PHONE}&text=I%20have%20submitted%20the%20registration%20form.%20Kindly%20check%20and%20confirm.`, '_blank')}
              className="flex-1 bg-[#25D366] hover:bg-[#1ebe57] text-white"
              data-testid="success-whatsapp"
            >
              WhatsApp Us
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default RegistrationForm;
